import { ApprovalRule } from "@p0security/shared/types/workflow/types";
import { hasApprovalOptions } from "@p0security/shared/types/workflow/util";
import { App } from "antd";
import { RoutingRuleEditorContext } from "components/Jit/Routing/store/RoutingRuleEditorContext";
import { EditableList } from "components/common/EditableList";
import { isEqual } from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { EditorBackButton } from "../EditorBackButton";
import { ApprovalOptionsPreview } from "../previews/ApprovalOptionsPreview";
import { ApprovalPreview } from "../previews/ApprovalPreview";
import { AccessRuleEditor } from "./AccessRuleEditor";

type Props = {
  /* Set to true when an access rule is being edited */
  onAccessRuleEditInProgress: (editing: boolean) => void;
};

export const AccessEditor: React.FC<Props> = ({
  onAccessRuleEditInProgress,
}) => {
  const { approval, setApproval } = useContext(RoutingRuleEditorContext);
  const { modal } = App.useApp();
  const [isAddingNewRule, setIsAddingNewRule] = useState(false);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);

  const [localRule, setLocalRule] = useState<ApprovalRule | undefined>(
    undefined
  );
  const isDirty = useMemo(() => {
    if (isAddingNewRule) return localRule !== undefined;
    if (editingRuleIndex !== null) {
      return !isEqual(approval[editingRuleIndex], localRule);
    }
    return false;
  }, [localRule, isAddingNewRule, editingRuleIndex, approval]);

  const backToAccessControl = useCallback(() => {
    setIsAddingNewRule(false);
    setEditingRuleIndex(null);
    setLocalRule(undefined);
    onAccessRuleEditInProgress(false);
  }, [onAccessRuleEditInProgress]);

  const handleStartAddNewAccessRule = useCallback(() => {
    setIsAddingNewRule(true);
    setEditingRuleIndex(null);
    setLocalRule(undefined);
    onAccessRuleEditInProgress(true);
  }, [onAccessRuleEditInProgress]);

  const handleStartEditAccessRule = useCallback(
    (index: number) => {
      setIsAddingNewRule(false);
      setEditingRuleIndex(index);
      setLocalRule(approval[index]);
      onAccessRuleEditInProgress(true);
    },
    [approval, onAccessRuleEditInProgress]
  );

  const handleEditAccessRule = useCallback(
    (rule: ApprovalRule) => {
      if (editingRuleIndex === null) {
        return;
      }

      const newApproval = [...approval];
      newApproval[editingRuleIndex] = rule;
      setApproval(newApproval);
      backToAccessControl();
    },
    [approval, backToAccessControl, editingRuleIndex, setApproval]
  );

  const handleAddNewAccessRule = useCallback(
    (rule: ApprovalRule) => {
      setApproval([...approval, rule]);
      backToAccessControl();
    },
    [approval, backToAccessControl, setApproval]
  );

  const handleDeleteAccessRule = useCallback(
    (index: number) => {
      modal.confirm({
        title: "Delete access rule?",
        content: "This action cannot be undone",
        okType: "danger",
        okText: "Yes, delete access rule.",
        onOk() {
          backToAccessControl();
          const newApproval = approval.filter((_, i) => i !== index);
          setApproval(newApproval);
        },
      });
    },
    [approval, modal, setApproval, backToAccessControl]
  );

  const renderApprovalRule = useCallback(
    (rule: ApprovalRule) => (
      <>
        <ApprovalPreview approval={rule} />
        {hasApprovalOptions(rule) && (
          <div style={{ marginLeft: 10 }}>
            <ApprovalOptionsPreview approvalOptions={rule.options} />
          </div>
        )}
      </>
    ),
    []
  );

  const isEditorOpen = isAddingNewRule || editingRuleIndex !== null;
  // Hide the list entirely when the editor is open and there is nothing to
  // show — otherwise the empty state visually overlaps with the open editor.
  const showList = !isEditorOpen || approval.length > 0;

  const cancelButton = useMemo(
    () => (
      <EditorBackButton
        onBack={backToAccessControl}
        isDirty={isDirty}
        isAdding={isAddingNewRule}
        name="Access Rule"
      />
    ),
    [backToAccessControl, isDirty, isAddingNewRule]
  );

  const newRuleContextValue = useMemo(
    () => ({ localRule, setLocalRule, isDirty, isNew: true }),
    [localRule, setLocalRule, isDirty]
  );

  const editRuleContextValue = useMemo(
    () => ({ localRule, setLocalRule, isDirty, isNew: false }),
    [localRule, setLocalRule, isDirty]
  );

  return (
    <div className="w-full">
      {showList && (
        <EditableList
          items={approval}
          onEditItem={handleStartEditAccessRule}
          onDeleteItem={handleDeleteAccessRule}
          onAddItem={handleStartAddNewAccessRule}
          renderItem={renderApprovalRule}
          addButtonText="Add New Access Rule"
          editing={isEditorOpen}
        />
      )}
      {isAddingNewRule ? (
        <LocalAccessRuleContext.Provider value={newRuleContextValue}>
          <AccessRuleEditor
            cancelButton={cancelButton}
            onSave={handleAddNewAccessRule}
          />
        </LocalAccessRuleContext.Provider>
      ) : editingRuleIndex !== null ? (
        <LocalAccessRuleContext.Provider value={editRuleContextValue}>
          <AccessRuleEditor
            cancelButton={cancelButton}
            onSave={handleEditAccessRule}
          />
        </LocalAccessRuleContext.Provider>
      ) : null}
    </div>
  );
};

type LocalAccessRule = {
  localRule: ApprovalRule | undefined;
  setLocalRule: (rule: ApprovalRule | undefined) => void;
  isDirty: boolean;
  isNew: boolean;
};

export const LocalAccessRuleContext = createContext<LocalAccessRule>({
  localRule: undefined,
  setLocalRule: () => {},
  isDirty: false,
  isNew: false,
});
